import React, { useState, useEffect, useCallback } from "react";
import { Button, IconButton, Snackbar, Alert, TextField } from "@mui/material";
import { Add, InfoOutlined } from "@mui/icons-material";
import { Link, useSearchParams } from "react-router-dom";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { differenceInDays, addDays } from "date-fns";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import TableFilter from "components/Shared/AppTable/TableFilter";
import HighlightedText from "components/Shared/HighlightedText";
import AppTable from "components/Shared/AppTable";
import BaseSelect from "components/Shared/Base/BaseSelect";
import ExportVehicleMileageExcel from "../VehicleFuel/VehicleFuelComponents/ExportVehicleMileageExcel";
import { useAppMeta, useTablePagination } from "hooks";
import { useLazyGetVehicleMileageReportQuery } from "store/services/reportApi";

const VehicleMileageListScreen = () => {
    const [sortOrder, setSortOrder] = useState("vehicle_id");
    const [sortDirection, setSortDirection] = useState("1");
    const [timeRange, setTimeRange] = useState("last7days");
    const [customFromDate, setCustomFromDate] = useState(
        moment().subtract(6, "days").startOf("day").toDate()
    );
    const [customToDate, setCustomToDate] = useState(moment().endOf("day").toDate());
    const [errorMessage, setErrorMessage] = useState("");
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } = useTablePagination();
    const [searchParams, setSearchParams] = useSearchParams();
    const keyword = searchParams.get("search") || "";

    const [fetchVehicleMileage, { data: vehicleMileageRs, isLoading, isFetching, error }] =
        useLazyGetVehicleMileageReportQuery();

    const breadcrumbs = [
        { title: "Reports", path: "/app/crm/reports/list" },
        { title: "Vehicle Mileage Report" },];

    const columns = [
        {
            name: "Vehicle ID",
            selector: (row) => row?.vehicle?.vehicle_id,
            cell: (row) =>
                row?.vehicle?.vehicle_id ? (
                    <HighlightedText text={row.vehicle.vehicle_id} highlight={keyword} />
                ) : (
                    "N/A"
                ),
        },
        {
            name: "Registration Number",
            selector: (row) => row?.vehicle?.registration_number,
            cell: (row) =>
                row?.vehicle?.registration_number ? (
                    <HighlightedText text={row.vehicle.registration_number} highlight={keyword} />
                ) : (
                    "N/A"
                ),
        },
        {
            name: "Recent Mileage",
            selector: (row) => row?.summary?.recentMileage,
            cell: (row) => (
                <span className="text-sm font-bold text-green-600 dark:text-green-400">
                    {row?.summary?.recentMileage ? `${row.summary.recentMileage} km/L` : "N/A"}
                </span>
            ),
        },
        {
            name: "Average Mileage",
            selector: (row) => row?.summary?.averageMileage,
            cell: (row) => (
                <span className="text-sm">
                    {row?.summary?.averageMileage ? `${row.summary.averageMileage} km/L` : "N/A"}
                </span>
            ),
        },
        {
            name: "Total Fuel Quantity",
            selector: (row) => row?.summary?.totalFuelQuantityAll,
            cell: (row) => (
                <span className="text-sm">
                    {row?.summary?.totalFuelQuantityAll ? `${row.summary.totalFuelQuantityAll} L` : "N/A"}
                </span>
            ),
        },
        {
            name: "Total Fuel Cost",
            selector: (row) => row?.summary?.totalFuelCost,
            cell: (row) => (
                <span className="text-sm">
                    Rs. {row?.summary?.totalFuelCost?.toFixed(2) || "N/A"}
                </span>
            ),
        },
        {
            name: "Actions",
            cell: (row) => (
                <Link to={`/app/crm/vehicle-fuel/vehicle/${row?.vehicle?._id}/records`}>
                    <IconButton size="small" aria-label="View Vehicle Fuel Records" color="primary">
                        <InfoOutlined />
                    </IconButton>
                </Link>
            ),
            ignoreRowClick: true,
            right: true,
        },
    ];

    const getDateRange = useCallback((range, from, to) => {
        const today = moment().startOf("day");
        switch (range.toLowerCase()) {
            case "last7days":
                return {
                    startDate: moment().subtract(6, "days").startOf("day").format("YYYY-MM-DD"),
                    endDate: today.endOf("day").format("YYYY-MM-DD"),
                };
            case "onemonth":
                return {
                    startDate: moment().subtract(1, "month").startOf("day").format("YYYY-MM-DD"),
                    endDate: today.endOf("day").format("YYYY-MM-DD"),
                };
            case "custom":
                if (from && to && moment(from).isValid() && moment(to).isValid()) {
                    if (moment(to).isBefore(moment(from))) {
                        setErrorMessage("Invalid date range. Ensure 'To' date is after 'From' date.");
                        return { startDate: null, endDate: null };
                    }
                    if (differenceInDays(new Date(to), new Date(from)) > 90) {
                        setErrorMessage("Date range cannot exceed 90 days.");
                        return { startDate: null, endDate: null };
                    }
                    return {
                        startDate: moment(from).format("YYYY-MM-DD"),
                        endDate: moment(to).format("YYYY-MM-DD"),
                    };
                }
                return { startDate: null, endDate: null };
            default:
                return {
                    startDate: moment().subtract(6, "days").startOf("day").format("YYYY-MM-DD"),
                    endDate: today.endOf("day").format("YYYY-MM-DD"),
                };
        }
    }, []);

    const fetchReportData = useCallback(() => {
        const { startDate, endDate } = getDateRange(timeRange, customFromDate, customToDate);

        const params = {
            page: currentPage,
            limit: perPage,
            sortOrder: `${sortOrder}:${sortDirection}`,
            ...(keyword && { keyword }),
            ...(startDate && { startDate }),
            ...(endDate && { endDate }),
        };

        // Only fetch if startDate and endDate are valid
        if (!startDate || !endDate) {
            return;
        }

        fetchVehicleMileage({ params });

        // Update URL search parameters
        const newParams = new URLSearchParams(searchParams);
        if (startDate) newParams.set("startDate", startDate);
        else newParams.delete("startDate");
        if (endDate) newParams.set("endDate", endDate);
        else newParams.delete("endDate");
        setSearchParams(newParams, { replace: true });
    }, [
        timeRange,
        customFromDate,
        customToDate,
        currentPage,
        perPage,
        sortOrder,
        sortDirection,
        keyword,
        fetchVehicleMileage,
        setSearchParams,
        searchParams,
        getDateRange,
    ]);

    useEffect(() => {
        fetchReportData();
    }, [fetchReportData]);

    useAppMeta("Vehicle Mileage Report");

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <BreadcrumbLayout breadcrumbs={breadcrumbs}>
                <BreadcrumbLayout.Error error={error} />
                <BreadcrumbLayout.Paper>

                    <div className="flex gap-2 flex-wrap justify-end items-center px-2 pt-2">
                        <BaseSelect
                            label="Range"
                            value={timeRange}
                            onChange={(e) => {
                                setTimeRange(e.target.value);
                                if (e.target.value !== "custom") {
                                    setCustomFromDate(null);
                                    setCustomToDate(null);
                                    setErrorMessage("");
                                } else {
                                    setCustomFromDate(moment().subtract(6, "days").startOf("day").toDate());
                                    setCustomToDate(moment().endOf("day").toDate());
                                }
                            }}
                            options={[
                                { label: "Last 7 Days", value: "last7days" },
                                { label: "One Month", value: "onemonth" },
                                { label: "Custom", value: "custom" },
                            ]}
                            getOptionLabel={(opt) => opt.label}
                            getOptionValue={(opt) => opt.value}
                            size="small"
                            sx={{ minWidth: 140 }}
                        />

                        {timeRange === "custom" && (
                            <>
                                <DatePicker
                                    label="From"
                                    value={customFromDate}
                                    onChange={(newValue) => {
                                        setCustomFromDate(newValue);
                                        setErrorMessage("");
                                        if (customToDate && differenceInDays(customToDate, newValue) > 90) {
                                            setCustomToDate(addDays(newValue, 90));
                                            setErrorMessage("Date range adjusted to maximum 90 days.");
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} size="small" sx={{ width: 140 }} />
                                    )}
                                    maxDate={customToDate || new Date()}
                                    disableFuture
                                />
                                <DatePicker
                                    label="To"
                                    value={customToDate}
                                    onChange={(newValue) => {
                                        setCustomToDate(newValue);
                                        setErrorMessage("");
                                        if (customFromDate && differenceInDays(newValue, customFromDate) > 90) {
                                            setCustomFromDate(addDays(newValue, -90));
                                            setErrorMessage("Date range adjusted to maximum 90 days.");
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField {...params} size="small" sx={{ width: 140 }} />
                                    )}
                                    minDate={customFromDate}
                                    maxDate={new Date()}
                                    disableFuture
                                />
                            </>
                        )}
                        <BaseSelect
                            label="Sort Direction"
                            value={sortDirection}
                            onChange={(e) => setSortDirection(e.target.value)}
                            options={[
                                { label: "Ascending", value: "1" },
                                { label: "Descending", value: "-1" },
                            ]}
                            getOptionLabel={(opt) => opt.label}
                            getOptionValue={(opt) => opt.value}
                            size="small"
                            sx={{ minWidth: 140 }}
                        />

                        <TableFilter
                            searchLabel="Vehicle ID"
                            disabled={isLoading || isFetching}
                            initialFilters={0}
                            filterAlignment="right"
                            exportComponent={ExportVehicleMileageExcel}

                        />
                    </div>

                    <AppTable
                        columns={columns}
                        data={vehicleMileageRs?.data ?? []}
                        progressPending={isLoading || isFetching}
                        paginationServer
                        paginationPerPage={perPage}
                        paginationTotalRows={vehicleMileageRs?.totalItems ?? 0}
                        paginationDefaultPage={currentPage}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        noDataComponent={
                            <div className="text-center py-8">
                                <p className="text-gray-500">
                                    {(!customFromDate || !customToDate) && timeRange === "custom"
                                        ? "Please select a valid date range to view mileage data."
                                        : "No mileage data available for the selected date range."}
                                </p>
                            </div>
                        }
                    />

                    <Snackbar
                        open={!!errorMessage}
                        autoHideDuration={6000}
                        onClose={() => setErrorMessage("")}
                    >
                        <Alert severity="error" onClose={() => setErrorMessage("")}>
                            {errorMessage}
                        </Alert>
                    </Snackbar>
                </BreadcrumbLayout.Paper>
            </BreadcrumbLayout>
        </LocalizationProvider>
    );
};


export default VehicleMileageListScreen;